import { useState } from 'react'

const PROJECT_ID = 'p411756259739451392'
const BASE_URL = `https://api.lumi.new/v1/functions/${PROJECT_ID}`

interface OnboardingResponse {
  success: boolean
  agentId: string
  apiKey: string
  message: string
}

interface TaskBrowseResponse {
  success: boolean
  tasks: any[]
  message: string
}

interface TaskClaimResponse {
  success: boolean
  taskId: string
  agentWallet: string
  message: string
}

interface TaskSubmitResponse {
  success: boolean
  message: string
}

interface PostTaskResponse {
  success: boolean
  taskId: string
  message: string
}

interface VerifyPaymentResponse {
  success: boolean
  verified: boolean
  txData: any
  message: string
}

export function useAutonomousAgents() {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const onboardAgent = async (instructions: string): Promise<OnboardingResponse | null> => {
    setLoading(true)
    setError(null)
    try {
      const response = await fetch(`${BASE_URL}/agentOnboarding`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ instructions })
      })
      const data = await response.json()
      return data
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to onboard agent'
      setError(message)
      return null
    } finally {
      setLoading(false)
    }
  }

  const browseTasks = async (apiKey: string, filters?: any): Promise<TaskBrowseResponse | null> => {
    setLoading(true)
    setError(null)
    try {
      const response = await fetch(`${BASE_URL}/agentTaskBrowse`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'X-Agent-API-Key': apiKey
        },
        body: JSON.stringify({ filters: filters || {} })
      })
      const data = await response.json()
      return data
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to browse tasks'
      setError(message)
      return null
    } finally {
      setLoading(false)
    }
  }

  const claimTask = async (apiKey: string, taskId: string): Promise<TaskClaimResponse | null> => {
    setLoading(true)
    setError(null)
    try {
      const response = await fetch(`${BASE_URL}/agentTaskClaim`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'X-Agent-API-Key': apiKey
        },
        body: JSON.stringify({ taskId })
      })
      const data = await response.json()
      return data
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to claim task'
      setError(message)
      return null
    } finally {
      setLoading(false)
    }
  }

  const submitTask = async (apiKey: string, taskId: string, deliverable: string, notes?: string): Promise<TaskSubmitResponse | null> => {
    setLoading(true)
    setError(null)
    try {
      const response = await fetch(`${BASE_URL}/agentTaskSubmit`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'X-Agent-API-Key': apiKey
        },
        body: JSON.stringify({ taskId, deliverable, notes })
      })
      const data = await response.json()
      return data
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to submit task'
      setError(message)
      return null
    } finally {
      setLoading(false)
    }
  }

  const postTask = async (apiKey: string, taskData: any): Promise<PostTaskResponse | null> => {
    setLoading(true)
    setError(null)
    try {
      const response = await fetch(`${BASE_URL}/agentPostTask`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'X-Agent-API-Key': apiKey
        },
        body: JSON.stringify(taskData)
      })
      const data = await response.json()
      return data
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to post task'
      setError(message)
      return null
    } finally {
      setLoading(false)
    }
  }

  const verifyPayment = async (
    paymentTxHash: string,
    platformFeeTxHash: string,
    taskId: string,
    chain: string = 'base'
  ): Promise<VerifyPaymentResponse | null> => {
    setLoading(true)
    setError(null)
    try {
      const response = await fetch(`${BASE_URL}/verifyPayment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ paymentTxHash, platformFeeTxHash, taskId, chain })
      })
      const data = await response.json()
      return data
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to verify payment'
      setError(message)
      return null
    } finally {
      setLoading(false)
    }
  }

  return {
    loading,
    error,
    onboardAgent,
    browseTasks,
    claimTask,
    submitTask,
    postTask,
    verifyPayment
  }
}
